import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import FormTemplatePickerModal from './FormTemplatePickerModal'
import { updateTask, getTask } from 'services/taskService'
import { updateProgrammeObligation } from 'services/ProgrammeService'
import { updateTaskRoutine } from 'features/taskManagement/ducks/actions'

const obligationIdOf = task =>
  task?.metadata?.programmeObligationId || task?.metadata?.programme_obligation_id || null

const TaskFormQuickEdit = ({ task, children, onSaved }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pending, setPending] = useState(null)
  const [applyToOpen, setApplyToOpen] = useState(false)
  const obligationId = obligationIdOf(task)
  const bpId = task?.building?.id || task?.buildingPassportId
  const obligationName = task?.complianceObligation?.name || task?.title
  const currentFormId = task?.formTemplate?.id || ''

  const refreshTask = async () => {
    const res = await getTask(task.id)
    dispatch(updateTaskRoutine.success(res.data?.data || res.data))
    onSaved?.()
  }

  const saveTaskForm = async () => {
    setSaving(true)
    try {
      await updateTask({ id: task.id, formTemplateId: pending?.id || null })
      await refreshTask()
      toast.success(pending?.id ? 'Form updated for this task.' : 'Form removed from this task.')
      setPending(null)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update the form. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const saveObligationForm = async () => {
    if (!bpId || !obligationId) return
    setSaving(true)
    try {
      await updateProgrammeObligation(bpId, obligationId, {
        form_template_id: pending?.id || null,
        apply_form_to_open_tasks: applyToOpen
      })
      await refreshTask()
      toast.success(
        applyToOpen
          ? 'Obligation updated. This form now applies to all future and current open tasks.'
          : 'Obligation updated. This form now applies to all future tasks for it.'
      )
      setPending(null)
      setApplyToOpen(false)
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        'Could not update the obligation. You may not have permission to manage it.'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleSelect = template => {
    setOpen(false)
    if (saving) return
    const picked = { id: template?.id || null, name: template?.name || null }
    if ((picked.id || '') === currentFormId) return
    if (obligationId) {
      setApplyToOpen(false)
      setPending({ ...picked, kind: 'obligation' })
      return
    }
    if (currentFormId) {
      setPending({ ...picked, kind: 'task' })
      return
    }
    setPending(picked)
    setTimeout(saveTaskForm, 0)
  }

  const closeConfirm = () => { if (!saving) { setPending(null); setApplyToOpen(false) } }

  return (
    <>
      <span
        onClick={e => { e?.stopPropagation?.(); setOpen(true) }}
        role='button'
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setOpen(true) } }}
        title='Change the form for this task'
        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', minWidth: 0 }}
      >
        {children}
      </span>

      <FormTemplatePickerModal
        open={open}
        onClose={() => setOpen(false)}
        value={currentFormId}
        buildingPassportId={bpId}
        onSelect={handleSelect}
        allowNone
      />

      {pending?.kind === 'obligation' && createPortal(
        <Backdrop onClick={closeConfirm}>
          <Dialog onClick={e => e.stopPropagation()}>
            <DialogTitle>This form is set by the compliance obligation</DialogTitle>
            <DialogText>
              The form on this task comes from the compliance obligation
              {obligationName ? <> <strong>“{obligationName}”</strong></> : null}, not from this task on its own.
              Changing it updates the obligation. Choose how far to apply the new form:
            </DialogText>
            <ChoiceList>
              <Choice htmlFor='applyFuture' $active={!applyToOpen}>
                <input
                  type='radio'
                  name='applyScope'
                  id='applyFuture'
                  checked={!applyToOpen}
                  onChange={() => setApplyToOpen(false)}
                />
                <ChoiceBody>
                  <ChoiceTitle>New tasks only <ChoiceTag>Recommended</ChoiceTag></ChoiceTitle>
                  <ChoiceText>
                    Every new task this obligation creates will use
                    {pending.name ? <> “{pending.name}”</> : ' no form'}.
                    This task and any others already open keep their current form and any answers already filled in.
                  </ChoiceText>
                </ChoiceBody>
              </Choice>
              <Choice htmlFor='applyAll' $active={applyToOpen} $warn>
                <input
                  type='radio'
                  name='applyScope'
                  id='applyAll'
                  checked={applyToOpen}
                  onChange={() => setApplyToOpen(true)}
                />
                <ChoiceBody>
                  <ChoiceTitle>New tasks and all open tasks now</ChoiceTitle>
                  <ChoiceText>
                    Also switch this task and every other unfinished task for this obligation to the new form straight away.{' '}
                    <strong>Any answers already filled in on those tasks are cleared, and this cannot be undone.</strong>
                  </ChoiceText>
                </ChoiceBody>
              </Choice>
            </ChoiceList>
            <DialogActions>
              <CancelButton type='button' onClick={closeConfirm} disabled={saving}>Cancel</CancelButton>
              <ConfirmButton type='button' onClick={saveObligationForm} disabled={saving}>
                {saving ? 'Updating…' : (applyToOpen ? 'Update obligation and open tasks' : 'Update obligation')}
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        </Backdrop>,
        document.body
      )}

      {pending?.kind === 'task' && createPortal(
        <Backdrop onClick={closeConfirm}>
          <Dialog onClick={e => e.stopPropagation()}>
            <DialogTitle>Change this task’s form?</DialogTitle>
            <WarnBox>
              Changing the form <strong>clears any answers already entered</strong> on this task. This cannot be undone.
            </WarnBox>
            <DialogText>
              This task will use {pending.name ? <>“{pending.name}”</> : 'no form'} from now on.
            </DialogText>
            <DialogActions>
              <CancelButton type='button' onClick={closeConfirm} disabled={saving}>Cancel</CancelButton>
              <ConfirmButton type='button' onClick={saveTaskForm} disabled={saving}>
                {saving ? 'Changing…' : 'Change form'}
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </>
  )
}

export default TaskFormQuickEdit

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 19, 43, 0.45);
  z-index: 100050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 460px;
  padding: 22px 24px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
`

const DialogTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 700;
  color: #0c1229;
`

const DialogText = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.55;
  color: #3d3d47;

  strong { color: #0c1229; }
`

const WarnBox = styled.div`
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #7c2d12;

  strong { color: #7c2d12; }
`

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ $active, $warn }) => ($active ? ($warn ? '#f59e0b' : '#33becc') : '#e6e8ee')};
  background: ${({ $active, $warn }) => ($active ? ($warn ? '#fff7ed' : '#e6f7f9') : '#fff')};
  cursor: pointer;
  transition: border-color .12s ease, background .12s ease;

  input { margin-top: 3px; flex-shrink: 0; cursor: pointer; width: 18px; height: 18px; }
`

const ChoiceBody = styled.div`
  min-width: 0;
`

const ChoiceTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #0c1229;
`

const ChoiceTag = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #15803d;
  background: #dcfce7;
  padding: 1px 7px;
  border-radius: 999px;
`

const ChoiceText = styled.div`
  margin-top: 3px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4b5563;

  strong { color: #b45309; }
`

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`

const CancelButton = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  background: #fff;
  color: #3d3d47;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const ConfirmButton = styled.button`
  min-height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background: #2aa6b3; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`
